import { t } from "i18next";

import Alert, { AlertTypes } from "@components/Alert/Alert";
import FileUpload from "@components/FileUpload/FileUpload";
import Thumbnail from "@components/Thumbnail/Thumbnail";
import { MediaObject, STATUS } from "@redux/services/types";
import styles from "./Media.module.scss";
import { Panorama } from "@livingmap/hippo";
import { useRef, useState } from "react";

import compassImage from "./compass_custom.svg";

interface Props {
  dataQA: string;
  media: MediaObject[] | null;
  onFileAdd: (files: File) => void;
  onCameraPositionChange: (position: CameraPosition) => void;
  onPanoramicFileAdd: (files: File) => void;
  onDeleteClick: (file: MediaObject) => void;
  status: string;
}

type CameraPosition = {
  lon: number;
  lat: number;
  phi: number;
  theta: number;
};

const Media: React.FC<Props> = ({
  dataQA,
  media,
  onFileAdd,
  onCameraPositionChange,
  onPanoramicFileAdd,
  onDeleteClick,
  status,
}) => {
  const originalMedia = media?.find(
    (mediaObject) => mediaObject.id === "original",
  );
  const panoramicMedia = media?.find((mediaObject) => mediaObject.id === "360");

  const [showPanorama, setShowPanorama] = useState(false);
  const cameraPositionRef = useRef<CameraPosition>(
    panoramicMedia?.camera_position || {
      lon: 0,
      lat: 0,
      phi: 0,
      theta: 0,
    },
  );

  const tips = (
    <ul>
      <li>{t("edit_views.features.sections.media.tips.list_items.0")}</li>
      <li>{t("edit_views.features.sections.media.tips.list_items.1")}</li>
      <li>{t("edit_views.features.sections.media.tips.list_items.2")}</li>
      <li>{t("edit_views.features.sections.media.tips.list_items.3")}</li>
    </ul>
  );

  const parnoramicTips = (
    <ul>
      <li>{t("edit_views.features.sections.media.tips.360_list_items.1")}</li>
      <li>{t("edit_views.features.sections.media.tips.360_list_items.2")}</li>
      <li>{t("edit_views.features.sections.media.tips.360_list_items.3")}</li>
    </ul>
  );

  const isEditable = status === STATUS.PUBLISHED || status === STATUS.DRAFT;

  return (
    <div data-qa={dataQA} className={styles.container}>
      <h5 className={styles.heading}>
        {t("edit_views.features.sections.media.title")}
      </h5>

      <div className={styles.mediaUploadContainer}>
        {originalMedia ? (
          <>
            <p className={styles.description}>
              {t("edit_views.features.sections.media.description_with_image")}
            </p>
            <Thumbnail
              dataQA="feature-image"
              key={originalMedia.id}
              image={originalMedia.url}
              onDeleteClick={() => onDeleteClick(originalMedia)}
            />
          </>
        ) : (
          <div>
            <p className={styles.description}>
              {t("edit_views.features.sections.media.description")}
            </p>
            <FileUpload
              dataQA="file-upload"
              onFileUpload={onFileAdd}
              maxFileSizeInBytes={104857600}
              disabled={!isEditable}
            />
            <div className={styles.alertContainer}>
              <Alert
                dataQA="tips-alert"
                title={t("edit_views.features.sections.media.tips.title")}
                description={tips}
                type={AlertTypes.INFO_ALT}
              />
            </div>
          </div>
        )}
        {panoramicMedia ? (
          <>
            <div className={styles.panoramicHeaderWrapper}>
              <h5 className={styles.heading}>
                {t("edit_views.features.sections.media.360_upload_title")}
              </h5>
              <p className={styles.description}>
                {t("edit_views.features.sections.media.360_description")}
              </p>
            </div>
            <Thumbnail
              dataQA="feature-image"
              key={panoramicMedia.id}
              image={panoramicMedia.url}
              onDeleteClick={() => onDeleteClick(panoramicMedia)}
              onClickSetOrientation={() => setShowPanorama(true)}
            />
            {showPanorama && (
              <Panorama
                dataQA="panorama-view"
                mode="edit"
                imageUrl={panoramicMedia.url}
                initialCameraPosition={cameraPositionRef.current}
                className={styles.panorama}
                buildingBlockLabel={t("poi.panorama_building_block")}
                customCompassImage={compassImage}
                onClose={() => setShowPanorama(false)}
                onSaveOrientation={(position) => {
                  cameraPositionRef.current = position;
                  setShowPanorama(false);
                  onCameraPositionChange(position);
                }}
              />
            )}
          </>
        ) : (
          <div>
            <h5 className={styles.heading}>
              {t("edit_views.features.sections.media.360_upload_title")}
            </h5>
            <p className={styles.description}>
              {t("edit_views.features.sections.media.360_description")}
            </p>
            <FileUpload
              dataQA="360-file-upload"
              onFileUpload={onPanoramicFileAdd}
              maxFileSizeInBytes={104857600}
              disabled={!isEditable}
            />
            <div className={styles.alertContainer}>
              <Alert
                dataQA="360-tips-alert"
                title={t("edit_views.features.sections.media.tips.360_title")}
                description={parnoramicTips}
                type={AlertTypes.INFO_ALT}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Media;
